import {
  type ColumnDef,
  functionalUpdate,
  getCoreRowModel,
  getSortedRowModel,
  type RowData,
  type SortingState,
  type Updater,
  useReactTable,
} from "@tanstack/react-table";
import { type DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { Children, isValidElement, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLayoutMeasure, useObservedElementRef } from "@tellescope/react-hooks";
import { buildGridTemplate, resolveColumnContentWidths, resolveColumnShares, sumMinWidths } from "./columnLayout";
import type { TableContextValue, TableHeaderSelection } from "./TableContext";
import { TableEmpty } from "./TableEmpty";
import { TableFooter } from "./TableFooter";
import { TableFooterRow } from "./TableFooterRow";
import type { TableColumn, TableColumnLayout, TableColumnWidths, TableProps, TableRowReorderItem } from "./types";
import { useColumnResize } from "./useColumnResize";

const DEFAULT_ROW_HEIGHT = 48;
const EMPTY_COLUMN_WIDTHS: TableColumnWidths = {};
const GRID_WIDTH_COMMIT_KEY = "grid-width";

const readGridWidth = (grid: HTMLElement) => grid.clientWidth;
const sameGridWidth = (a: number, b: number) => a === b;
const UTILITY_COLUMN_PX = 40;
const EMPTY_SORTING: SortingState = [];

const toColumnDef = <TRow extends RowData>(column: TableColumn<TRow>): ColumnDef<TRow, unknown> => {
  const base = {
    id: column.id,
    header: column.label ?? column.id,
    enableSorting: column.sortable !== false,
    meta: { overflow: column.overflow },
    ...(column.cell !== undefined ? { cell: column.cell } : {}),
    ...(column.sortingFn !== undefined ? { sortingFn: column.sortingFn } : {}),
    ...(column.sortUndefined !== undefined ? { sortUndefined: column.sortUndefined } : {}),
  };
  if (column.accessorFn) return { ...base, accessorFn: column.accessorFn };
  if (column.accessorKey !== undefined) return { ...base, accessorKey: column.accessorKey };
  return base;
};

const partitionChildren = (children: ReactNode) =>
  Children.toArray(children).reduce<{
    above: ReactNode[];
    empty: ReactNode | undefined;
    footerRow: ReactNode | undefined;
    footer: ReactNode | undefined;
  }>(
    (acc, child) => {
      if (isValidElement(child) && child.type === TableEmpty) acc.empty = child;
      else if (isValidElement(child) && child.type === TableFooterRow) acc.footerRow = child;
      else if (isValidElement(child) && child.type === TableFooter) acc.footer = child;
      else acc.above.push(child);
      return acc;
    },
    { above: [], empty: undefined, footerRow: undefined, footer: undefined }
  );

export const useTableLogic = <TRow extends RowData>({
  rows,
  columns,
  getRowId,
  sorting = EMPTY_SORTING,
  onSortingChange,
  manualSorting = false,
  selectedRowIds,
  onToggleRow,
  onToggleAllRows,
  columnWidths,
  onColumnWidthsChange,
  onRowReorder,
  onBottomReached,
  hasMore = false,
  isLoading = false,
  isLoadingMore = false,
  rowHeight = DEFAULT_ROW_HEIGHT,
  expandMaxRows,
  structuralRowsColor = "default",
  structuralRowsText = "default",
  children,
}: TableProps<TRow>) => {
  const expandMaxHeight = expandMaxRows !== undefined ? expandMaxRows * rowHeight : undefined;
  const columnDefs = useMemo(() => columns.map(toColumnDef), [columns]);
  const columnVisibility = useMemo(
    () =>
      columns.reduce<Record<string, boolean>>((acc, column) => {
        acc[column.id] = column.visible !== false;
        return acc;
      }, {}),
    [columns]
  );

  const handleSortingChange = useCallback(
    (updater: Updater<SortingState>) => onSortingChange?.(functionalUpdate(updater, sorting)),
    [onSortingChange, sorting]
  );

  const table = useReactTable<TRow>({
    data: rows,
    columns: columnDefs,
    getRowId,
    state: { sorting, columnVisibility },
    onSortingChange: handleSortingChange,
    enableSorting: onSortingChange !== undefined,
    manualSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const canSelect = onToggleRow !== undefined;
  const canReorder = onRowReorder !== undefined;

  const layoutById = useMemo(
    () =>
      columns.reduce<Record<string, TableColumnLayout>>((acc, column) => {
        acc[column.id] = column;
        return acc;
      }, {}),
    [columns]
  );
  const visibleColumns = table.getVisibleLeafColumns();
  const layoutColumns = useMemo(
    () =>
      visibleColumns.reduce<TableColumnLayout[]>((acc, column) => {
        const layout = layoutById[column.id];
        if (layout) acc.push(layout);
        return acc;
      }, []),
    [visibleColumns, layoutById]
  );
  const leadingFixedPx = useMemo(
    () => [...(canReorder ? [UTILITY_COLUMN_PX] : []), ...(canSelect ? [UTILITY_COLUMN_PX] : [])],
    [canReorder, canSelect]
  );

  const shares = useMemo(() => resolveColumnShares(layoutColumns, columnWidths), [layoutColumns, columnWidths]);
  // The grid's width changes only with its container (never during a drag), so no commit needs to re-read it.
  const { ref: headerRef, value: gridWidth } = useLayoutMeasure<number, HTMLDivElement>(readGridWidth, sameGridWidth, {
    commitKey: GRID_WIDTH_COMMIT_KEY,
  });
  const { dragShares, resizingColumnId, getResizeHandleProps } = useColumnResize({
    columns: layoutColumns,
    shares,
    headerRef,
    onColumnWidthsChange,
  });
  const effectiveShares = dragShares ?? shares;
  const columnContentWidths = useMemo(
    () =>
      gridWidth === undefined
        ? EMPTY_COLUMN_WIDTHS
        : resolveColumnContentWidths(layoutColumns, effectiveShares, leadingFixedPx, gridWidth),
    [gridWidth, layoutColumns, effectiveShares, leadingFixedPx]
  );
  const gridTemplateColumns = useMemo(
    () => buildGridTemplate(layoutColumns, effectiveShares, leadingFixedPx),
    [layoutColumns, effectiveShares, leadingFixedPx]
  );
  const minTableWidth = useMemo(() => sumMinWidths(layoutColumns, leadingFixedPx), [layoutColumns, leadingFixedPx]);
  const hasExpandColumn = layoutColumns.some((column) => column.overflow === "expand");

  const rowModel = table.getRowModel().rows;
  const rowIds = useMemo(() => rowModel.map((row) => row.id), [rowModel]);
  const selectedSet = useMemo<ReadonlySet<string>>(() => new Set(selectedRowIds ?? []), [selectedRowIds]);
  const headerSelection = useMemo<TableHeaderSelection>(() => {
    if (rowIds.length === 0) return "none";
    const selectedCount = rowIds.filter((id) => selectedSet.has(id)).length;
    if (selectedCount === 0) return "none";
    return selectedCount === rowIds.length ? "all" : "some";
  }, [rowIds, selectedSet]);

  // The body owns both scroll axes so its vertical scrollbar runs alongside the rows only. The header
  // sits outside it, so it follows the body's horizontal scroll and reserves the width the body's
  // vertical scrollbar takes, or its columns would drift out of line with the cells beneath them.
  const { ref: viewportRef, element: viewportElement } = useObservedElementRef<HTMLDivElement>();
  const headerViewportRef = useRef<HTMLDivElement | null>(null);
  const [scrollbarGutter, setScrollbarGutter] = useState(0);
  useEffect(() => {
    if (!viewportElement) return;

    const measureGutter = () => setScrollbarGutter(viewportElement.offsetWidth - viewportElement.clientWidth);
    const followScroll = () => {
      if (headerViewportRef.current) headerViewportRef.current.scrollLeft = viewportElement.scrollLeft;
    };

    const observer = new ResizeObserver(measureGutter);
    observer.observe(viewportElement);
    measureGutter();
    viewportElement.addEventListener("scroll", followScroll);

    return () => {
      observer.disconnect();
      viewportElement.removeEventListener("scroll", followScroll);
    };
  }, [viewportElement]);

  const canLoadMore = hasMore && !isLoading && !isLoadingMore && onBottomReached !== undefined;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (!onRowReorder || !over || active.id === over.id) return;
      const oldIndex = rowIds.indexOf(String(active.id));
      const newIndex = rowIds.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) return;

      const moved = arrayMove(rowIds, oldIndex, newIndex);
      const items = moved.reduce<TableRowReorderItem[]>((acc, id, index) => {
        if (rowIds[index] !== id) acc.push({ id, index });
        return acc;
      }, []);
      onRowReorder(items);
    },
    [onRowReorder, rowIds]
  );

  const {
    above: childrenAbove,
    empty: emptyContent,
    footerRow: footerRowContent,
    footer: footerContent,
  } = partitionChildren(children);

  // Sorting reorders the whole row set at once, which is exactly the wholesale replacement `virtua`'s
  // positional size cache cannot follow when rows vary in height.
  const resetKey = hasExpandColumn
    ? sorting.map((sort) => `${sort.id}:${sort.desc ? "desc" : "asc"}`).join(",")
    : undefined;

  const contextValue = useMemo<TableContextValue>(
    () => ({
      layoutById,
      shares: effectiveShares,
      gridTemplateColumns,
      rowHeight,
      expandMaxHeight,
      hasExpandColumn,
      structuralRowsColor,
      structuralRowsText,
      hasFooterRow: footerRowContent !== undefined,
      canSelect,
      canReorder,
      selectedRowIds: selectedSet,
      headerSelection,
      onToggleRow,
      onToggleAllRows,
      getResizeHandleProps,
      resizingColumnId,
      columnContentWidths,
    }),
    [
      columnContentWidths,
      layoutById,
      effectiveShares,
      gridTemplateColumns,
      rowHeight,
      expandMaxHeight,
      hasExpandColumn,
      structuralRowsColor,
      structuralRowsText,
      footerRowContent,
      canSelect,
      canReorder,
      selectedSet,
      headerSelection,
      onToggleRow,
      onToggleAllRows,
      getResizeHandleProps,
      resizingColumnId,
    ]
  );

  return {
    table,
    contextValue,
    headerRef,
    headerViewportRef,
    viewportRef,
    scrollbarGutter,
    minTableWidth,
    onBottomReached: canLoadMore ? onBottomReached : undefined,
    isLoading,
    isLoadingMore,
    reorder: canReorder ? { sensors, onDragEnd: handleDragEnd, ids: rowIds } : undefined,
    childrenAbove,
    emptyContent,
    footerRowContent,
    footerContent,
    resetKey,
  };
};
